import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {
  catchError,
  Observable,
  throwError,
  finalize,
  map,
  BehaviorSubject,
} from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

/**
 * Interfaz para la solicitud de búsqueda de cliente
 */
export interface ClientSearchRequest {
  IdNumber: string;
  Name: string;
  ProducerNumber: number;
}

/**
 * Interfaz para la respuesta de búsqueda de cliente
 */
export interface ClientSearchResponse {
  ClientName: string;
  clientcode: number;
}

/**
 * Servicio para la identificación y búsqueda de clientes
 */
@Injectable({
  providedIn: 'root',
})
export class IdentificacionService {
  private url = environment.int365_dom + '/api/v1/Lookup/search';
  private clientCodeKey = 'clientCode';
  private noEncontradoSubject = new BehaviorSubject<boolean>(false);
  noEncontrado$ = this.noEncontradoSubject.asObservable();

  constructor(private http: HttpClient, private authService: AuthService) {}

  /**
   * Busca un cliente según los criterios proporcionados
   * @param request Objeto con los criterios de búsqueda
   * @returns Observable con la respuesta de la búsqueda
   */
  searchClient(request: ClientSearchRequest): Observable<ClientSearchResponse> {
    this.noEncontradoSubject.next(false);

    const containsOnlyNumbers = /^\d+$/.test(request.IdNumber);

    const params = {
      IdNumber: containsOnlyNumbers ? request.IdNumber : '',
      Name: containsOnlyNumbers ? '' : request.IdNumber,
      ProducerNumber: request.ProducerNumber.toString(),
    };

    return this.http
      .get<ClientSearchResponse>(this.url, {
        ...this.getHttpOptions(params),
        observe: 'response',
        responseType: 'json',
      })
      .pipe(
        catchError((error: any) => {
          try {
            console.error('Error en la búsqueda del cliente:', error);
            if (error.status === 404) {
              this.noEncontradoSubject.next(true);
            }
            return throwError(() => error);
          } catch (err) {
            console.error('Error al procesar el error:', err);
            return throwError(() => err);
          }
        }),
        finalize(() => {}),
        map((response: any) => {
          if (response.body?.clientcode) {
            localStorage.setItem(
              this.clientCodeKey,
              response.body.clientcode.toString()
            );
          }
          return response.body;
        })
      );
  }

  /**
   * Obtiene las opciones HTTP incluyendo headers y parámetros
   * @param params Parámetros opcionales para la petición
   * @returns Objeto con las opciones HTTP configuradas
   */
  private getHttpOptions(params?: any) {
    const token = this.authService.getToken();
    if (!token) throw new Error('No token found');

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return {
      headers,
      params: params
        ? new HttpParams({ fromObject: this.formatParams(params) })
        : undefined,
    };
  }

  /**
   * Formatea los parámetros para la petición HTTP
   * @param params Objeto con los parámetros a formatear
   * @returns Objeto con los parámetros formateados como strings
   */
  private formatParams(params: any): { [key: string]: string } {
    const formattedParams: { [key: string]: string } = {};

    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined) return;

      if (value instanceof Date) {
        formattedParams[key] = value.toISOString().split('T')[0];
      } else {
        formattedParams[key] = String(value);
      }
    });

    return formattedParams;
  }

  /**
   * Obtiene el código de cliente almacenado
   * @returns Código de cliente como número o null si no existe
   */
  getClientCode(): number | null {
    const clientCode = localStorage.getItem(this.clientCodeKey);
    return clientCode ? +clientCode : null;
  }

  /**
   * Reinicia el código de cliente y el estado de no encontrado
   */
  resetClientCode(): void {
    localStorage.removeItem(this.clientCodeKey);
    this.noEncontradoSubject.next(false);
  }
}
